// 동기화 엔진 — DESIGN §3 «동기화 주기·오프라인»
//  · base  = 서버가 확인해 준 행(커서 이후 변경을 받아 갱신)
//  · queue = 아직 서버가 받지 않은 변경(IndexedDB 에 저장 → 새로고침·오프라인에도 안 사라짐)
//  · view  = base 위에 queue 를 순서대로 덧칠한 것(낙관적 화면). 화면은 view 만 읽는다
// 주기: 변경 직후 즉시 · 화면 포커스 복귀 · 앞에 떠 있는 동안 15초 폴링 · 온라인 복귀
import { useSyncExternalStore } from "react";
import { api, HttpError } from "./api";
import { idbDel, idbGet, idbSet } from "./idb";
import { ENTITIES, St, type Entity, type Item, type Row, type Session, type Settings, type Staff, type Student, type Warn, DEFAULT_SETTINGS } from "./types";
import { payloadOf } from "../auth";

export const POLL_MS = 15_000;

type Tables = Record<Entity, Map<string, Row>>;
export type Mutation = {
  mid: string;
  entity: Entity;
  op: "upsert" | "delete";
  data: Row;
  /** 서버로 보내지 않는 화면용 힌트: 항목 체크가 어느 일정에서 일어났는지(자동 완료를 먼저 그리려고) */
  hint?: { sessionId?: string; by?: string };
  at: number;
};

export type SyncStatus = {
  loaded: boolean;      // 캐시든 서버든 한 번이라도 데이터를 받았나
  online: boolean;
  syncing: boolean;
  lastSync: number | null;
  pending: number;
  error: string | null;
};

export type View = {
  staff: Staff[];
  students: Student[];
  items: Item[];
  sessions: Session[];
  warns: Warn[];
  settings: Settings;
  status: SyncStatus;
  rev: number;
};

const empty = (): Tables => Object.fromEntries(ENTITIES.map((e) => [e, new Map<string, Row>()])) as Tables;

let base: Tables = empty();
let cursor: string | null = null;
let queue: Mutation[] = [];
let owner: string | null = null;
let token: string | null = null;
let status: SyncStatus = { loaded: false, online: true, syncing: false, lastSync: null, pending: 0, error: null };
let rev = 0;
let view: View = build();
const listeners = new Set<() => void>();

let onUnauthorized: () => void = () => {};
let notify: (msg: string, tone?: "info" | "error") => void = () => {};
export function setHandlers(h: { unauthorized?: () => void; notify?: (msg: string, tone?: "info" | "error") => void }) {
  if (h.unauthorized) onUnauthorized = h.unauthorized;
  if (h.notify) notify = h.notify;
}

/* ───────── view 계산 ───────── */

function overlay(): Tables {
  const t = Object.fromEntries(ENTITIES.map((e) => [e, new Map(base[e])])) as Tables;
  for (const m of queue) {
    const map = t[m.entity];
    const id = m.data.id;
    if (m.op === "delete") {
      map.delete(id);
      continue;
    }
    map.set(id, { ...(map.get(id) ?? {}), ...m.data });
    // 서버 규칙을 화면에 먼저 그린다(최종 판정은 서버): 시험지를 다 통과하면 그 일정 완료, 통과 취소하면 예정
    if (m.entity === "items" && m.hint?.sessionId) {
      const s = t.sessions.get(m.hint.sessionId) as Session | undefined;
      if (s && !s.deleted) {
        const left = [...t.items.values()].filter((i) => !i.deleted && i.studentId === s.studentId && !i.done).length;
        if (left === 0 && (s.status === St.PLAN || s.status === St.PARTIAL)) t.sessions.set(s.id, { ...s, status: St.DONE, closedBy: m.hint.by ?? null });
        else if (left > 0 && s.status === St.DONE) t.sessions.set(s.id, { ...s, status: St.PLAN, closedBy: null });
      }
    }
  }
  return t;
}

function build(): View {
  const t = overlay();
  const list = <T,>(e: Entity) => [...t[e].values()].filter((r) => !r.deleted) as unknown as T[];
  const settings = (list<Settings>("settings")[0] ?? DEFAULT_SETTINGS) as Settings;
  return {
    staff: list<Staff>("staff"),
    students: list<Student>("students").sort((a, b) => a.name.localeCompare(b.name, "ko")),
    items: list<Item>("items"),
    sessions: list<Session>("sessions"),
    warns: list<Warn>("warns"),
    settings: { ...DEFAULT_SETTINGS, ...settings },
    status: { ...status, pending: queue.length },
    rev: ++rev,
  };
}

function emit() {
  view = build();
  listeners.forEach((f) => f());
}
function setStatus(p: Partial<SyncStatus>) {
  status = { ...status, ...p };
  emit();
}

export function useView(): View {
  return useSyncExternalStore((f) => { listeners.add(f); return () => listeners.delete(f); }, () => view);
}
export const getView = () => view;

/* ───────── 저장(IndexedDB) ───────── */

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function saveCache() {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    const rows = Object.fromEntries(ENTITIES.map((e) => [e, [...base[e].values()]]));
    void idbSet("cache", { owner, cursor, rows, at: Date.now() });
  }, 300);
}
const saveQueue = () => void idbSet("queue", { owner, queue });

/* ───────── 변경(낙관적) ───────── */

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);
export const newId = uuid;

export function mutate(entity: Entity, op: "upsert" | "delete", data: Row, hint?: Mutation["hint"]) {
  queue.push({ mid: uuid(), entity, op, data, hint, at: Date.now() });
  saveQueue();
  emit();
  kick();
}

/* ───────── 서버와 주고받기 ───────── */

let inflight: Promise<void> | null = null;
let again = false;
let kickTimer: ReturnType<typeof setTimeout> | null = null;
function kick() {
  if (kickTimer) clearTimeout(kickTimer);
  kickTimer = setTimeout(() => void syncNow(), 40);
}

export async function syncNow(): Promise<void> {
  if (!token) return;
  if (inflight) {
    again = true;
    return inflight;
  }
  inflight = (async () => {
    do {
      again = false;
      setStatus({ syncing: true });
      try {
        await push();
        await pull();
        setStatus({ online: true, error: null, lastSync: Date.now(), loaded: true });
      } catch (e) {
        if (e instanceof HttpError && e.status === 401) {
          onUnauthorized();
          break;
        }
        setStatus({ online: !(e instanceof TypeError), error: e instanceof HttpError ? `서버 오류 ${e.status}` : "연결 안 됨" });
        break;
      } finally {
        setStatus({ syncing: false });
      }
    } while (again && token);
  })();
  try {
    await inflight;
  } finally {
    inflight = null;
  }
}

type MutRes = {
  applied: { mid: string; id: string; version: number | null }[];
  conflicts: { mid: string; id: string; reason?: string; server: Row | null }[];
  rejected?: { mid: string; id: string | null; reason: string }[];
  cursor: string;
};

async function push() {
  if (!queue.length) return;
  const batch = queue.slice(0, 200);
  // 같은 행을 한 묶음에서 여러 번 바꾸면 앞 변경이 올린 version 을 다음 변경의 baseVersion 으로 쓴다
  const expect = new Map<string, number | null>();
  const body = batch.map((m) => {
    const key = m.entity + ":" + m.data.id;
    const bv = expect.has(key) ? expect.get(key)! : (base[m.entity].get(m.data.id)?.version ?? null);
    expect.set(key, bv === null ? 1 : bv + 1);
    return { mid: m.mid, entity: m.entity, op: m.op, data: m.data, baseVersion: bv };
  });
  const res = await api<MutRes>("/api/mutations", { token, body: { mutations: body } });
  const byMid = new Map(batch.map((m) => [m.mid, m]));
  const done = new Set<string>();
  for (const a of res.applied) {
    const m = byMid.get(a.mid);
    done.add(a.mid);
    if (!m) continue;
    // 서버가 받은 값을 base 에 바로 반영(곧 pull 이 서버 행으로 덮는다) — 그사이 화면이 옛값으로 튀지 않게
    const map = base[m.entity];
    if (m.op === "delete") map.delete(m.data.id);
    else map.set(m.data.id, { ...(map.get(m.data.id) ?? {}), ...m.data, version: a.version ?? undefined });
  }
  let conflicted = 0;
  for (const c of res.conflicts) {
    done.add(c.mid);
    const m = byMid.get(c.mid);
    if (m && c.server) {
      if (c.server.deleted) base[m.entity].delete(c.id);
      else base[m.entity].set(c.id, c.server);
    }
    conflicted++;
  }
  for (const r of res.rejected ?? []) {
    done.add(r.mid);
    notify(`저장하지 못했어요: ${r.reason}`, "error");
  }
  queue = queue.filter((m) => !done.has(m.mid));
  saveQueue();
  saveCache();
  if (conflicted) notify("다른 선생님이 먼저 바꿨어요 — 최신 내용으로 맞췄습니다", "info");
  emit();
}

type SyncRes = { cursor: string; full?: boolean; changes: Record<Entity, Row[]> };

async function pull() {
  const q = cursor ? `?since=${encodeURIComponent(cursor)}` : "";
  const res = await api<SyncRes>("/api/sync" + q, { token });
  if (res.full || !cursor) base = empty();
  for (const e of ENTITIES) {
    for (const r of res.changes[e] ?? []) {
      if (r.deleted) base[e].delete(r.id);
      else base[e].set(r.id, r);
    }
  }
  cursor = res.cursor;
  saveCache();
}

/* ───────── 시작·정지 ───────── */

let pollTimer: ReturnType<typeof setInterval> | null = null;
const onVisible = () => { if (document.visibilityState === "visible") void syncNow(); };
const onFocus = () => void syncNow();
const onOnline = () => { setStatus({ online: true }); void syncNow(); };
const onOffline = () => setStatus({ online: false, error: "연결 안 됨" });

export async function startEngine(tok: string) {
  const sid = payloadOf(tok)?.sid ?? null;
  if (token === tok) return;
  token = tok;
  if (owner !== sid) {
    owner = sid;
    base = empty();
    cursor = null;
    queue = [];
    status = { loaded: false, online: true, syncing: false, lastSync: null, pending: 0, error: null };
    // 같은 직원의 캐시·미전송 큐만 되살린다(다른 직원이 이 기기로 로그인하면 버린다)
    const [cache, q] = await Promise.all([
      idbGet<{ owner: string; cursor: string | null; rows: Record<Entity, Row[]> }>("cache"),
      idbGet<{ owner: string; queue: Mutation[] }>("queue"),
    ]);
    if (token !== tok) return;
    if (cache && cache.owner === sid) {
      for (const e of ENTITIES) for (const r of cache.rows[e] ?? []) base[e].set(r.id, r);
      cursor = cache.cursor;
      status = { ...status, loaded: true };
    }
    if (q && q.owner === sid && Array.isArray(q.queue)) queue = [...q.queue, ...queue];
    emit();
  }
  if (!pollTimer) {
    pollTimer = setInterval(() => { if (document.visibilityState === "visible") void syncNow(); }, POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
  }
  void syncNow();
}

export function stopEngine(clear: boolean) {
  token = null;
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  document.removeEventListener("visibilitychange", onVisible);
  window.removeEventListener("focus", onFocus);
  window.removeEventListener("online", onOnline);
  window.removeEventListener("offline", onOffline);
  if (clear) {
    owner = null;
    base = empty();
    cursor = null;
    queue = [];
    void idbDel("cache");
    void idbDel("queue");
    status = { loaded: false, online: true, syncing: false, lastSync: null, pending: 0, error: null };
    emit();
  }
}
